import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { Option } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import { Pagination } from '@/common/types';
import { API_URL, IOrganizationNameDto } from '@/dtos';

interface EmployerResponse {
  id: number;
  name: string;
}

const QUERY_KEY = 'GET_EMPLOYERS_DROPDOWN_LIST';

const QUERY_KEY_BY_ID = 'GET_EMPLOYER_BY_ID';
interface Params {
  defaultEmployerId?: string;
  searchString: string;
  perPage?: number;
}
const DEFAULT_PER_PAGE = 20;
const useEmployersDropdownListQuery = (params: Params) => {
  const [page, setPage] = useState(0);
  const [lastLoadedPage, setLastLoadedPage] = useState(-1);
  const [list, setList] = useState<Option[]>([]);

  const { data, isLoading, isSuccess } = useQuery(
    [QUERY_KEY, page, params.searchString, params.perPage],
    ({ signal }) =>
      api.get<Pagination<EmployerResponse>>(API_URL.ORGANIZATIONS, {
        signal,
        params: {
          search_value: _.toLower(params.searchString.trim()),
          page: page,
          size: params.perPage || DEFAULT_PER_PAGE,
          type: OrganizationTypes.company,
        },
      }),
    {
      gcTime: 0,
      onSuccess: (data) => {
        if (lastLoadedPage < page) {
          const sourceList = data?.data?.content || [];
          const formattedList: Option[] = sourceList.map((item) => ({
            key: `${item.id}`,
            value: item.name,
            title: item.name,
          }));

          setList([...list, ...formattedList]);
          setLastLoadedPage(page);
        }
      },
    }
  );
  useEffect(() => {
    setPage(0);
    setList([]);
    setLastLoadedPage(-1);
  }, [params.searchString]);
  const { data: dataById } = useQuery(
    [QUERY_KEY_BY_ID, params.defaultEmployerId],
    () => api.get<IOrganizationNameDto>(API_URL.ORGANIZATIONS_NAME_BY_ID(params.defaultEmployerId || '')),
    {
      enabled: !!params.defaultEmployerId && !params.searchString,
    }
  );
  const defaultEmployer = useMemo<Option | null>(() => {
    const payload = dataById?.data;
    if (payload) {
      return {
        key: `${payload.id || ''}`,
        title: payload.name,
        value: payload.name,
      };
    }
    return null;
  }, [dataById?.data]);

  const total = useMemo(() => {
    return data?.data?.totalElements || undefined;
  }, [data?.data?.totalElements]);
  const totalPages = useMemo(() => data?.data?.totalPages || 0, [data]);

  const handleLoadMore = useCallback(() => {
    if (page + 1 < totalPages) {
      setPage(page + 1);
    }
  }, [page, totalPages]);

  return {
    data,
    defaultEmployer,
    handleLoadMore,
    options: list,
    total,
    isLoading,
    isSuccess,
  };
};

export default useEmployersDropdownListQuery;
